import { Building2, Home, Users, TrendingUp, ArrowRight, MapPin } from "lucide-react";
import { IndustryPageLayout } from "@/components/IndustryPageLayout";
import { Card, CardContent } from "@/components/ui/card";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/integrations/supabase/types";

type IndustryImage = Tables<'industry_images'>;

const RealEstatePage = () => {
  const [industryImages, setIndustryImages] = useState<IndustryImage[]>([]);

  useEffect(() => {
    const fetchIndustryImages = async () => {
      const { data, error } = await supabase
        .from('industry_images')
        .select('*')
        .eq('industry', 'real-estate')
        .order('display_order', { ascending: true });

      if (error) {
        console.error('Error fetching industry images:', error);
      } else {
        setIndustryImages(data || []);
      }
    };

    fetchIndustryImages();
  }, []);

  const industry = {
    title: "Real Estate",
    icon: Building2,
    gradient: "from-indigo-500 to-blue-500",
    description: "Generate property visualizations, staging concepts, and marketing materials that showcase potential and accelerate sales.",
    stats: "25% faster sales"
  };

  const heroContent = (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <Card className="bg-white/5 backdrop-blur-md border-white/10">
        <CardContent className="p-6 text-center">
          <Home className="w-8 h-8 text-indigo-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Property Visualization</h3>
          <p className="text-gray-300 text-sm">Virtual staging and renovation concepts</p>
        </CardContent>
      </Card>
      <Card className="bg-white/5 backdrop-blur-md border-white/10">
        <CardContent className="p-6 text-center">
          <MapPin className="w-8 h-8 text-indigo-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Location Marketing</h3>
          <p className="text-gray-300 text-sm">Showcase neighborhoods and amenities</p>
        </CardContent>
      </Card>
      <Card className="bg-white/5 backdrop-blur-md border-white/10">
        <CardContent className="p-6 text-center">
          <TrendingUp className="w-8 h-8 text-indigo-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Sales Acceleration</h3>
          <p className="text-gray-300 text-sm">Faster closings with compelling visuals</p>
        </CardContent>
      </Card>
    </div>
  );

  const imageGallery = (
    <div>
      <h3 className="text-2xl font-bold text-white text-center mb-8">AI-Generated Real Estate Examples</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {industryImages.length > 0 ? (
          industryImages.map((image) => (
            <Card key={image.id} className="bg-white/5 backdrop-blur-md border-white/10 overflow-hidden group hover:bg-white/10 transition-all duration-300">
              <div className="aspect-[4/3] relative overflow-hidden">
                <img 
                  src={image.image_url} 
                  alt={image.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              <CardContent className="p-4">
                <p className="text-sm text-gray-300">{image.title}</p>
              </CardContent>
            </Card>
          ))
        ) : (
          <div className="col-span-3 text-center text-gray-400 py-12">
            <Building2 className="w-12 h-12 mx-auto mb-4" />
            <p className="text-white">No real estate examples available yet</p>
            <p className="text-sm text-gray-400 mt-2">Images will be added soon</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <IndustryPageLayout
      industry={industry}
      heroContent={heroContent}
      mainContent={
        <div className="prose prose-invert max-w-none">
          <h2 className="text-3xl font-bold text-white mb-6">Transforming Real Estate Marketing</h2>
          
          <div className="text-gray-300 space-y-6 text-lg leading-relaxed">
            <p>
              The real estate industry relies heavily on visual appeal to drive sales and rentals. AI-generated imagery is revolutionizing how properties are marketed, allowing agents and developers to showcase potential before it becomes reality.
            </p>
            
            <h3 className="text-2xl font-semibold text-white mt-8 mb-4">The Power of Visual Real Estate Marketing</h3>
            <p>
              In today's competitive market, buyers and renters make decisions within seconds of viewing a property. High-quality visuals that showcase a property's potential can mean the difference between a quick sale and months on the market.
            </p>
            
            <h3 className="text-2xl font-semibold text-white mt-8 mb-4">AI Solutions for Real Estate</h3>
            <ul className="list-disc list-inside space-y-3 text-gray-300">
              <li><strong className="text-white">Virtual Staging:</strong> Transform empty spaces into furnished, inviting homes</li>
              <li><strong className="text-white">Renovation Concepts:</strong> Show potential buyers what properties could become</li>
              <li><strong className="text-white">Lifestyle Marketing:</strong> Create aspirational imagery around properties</li>
              <li><strong className="text-white">Architectural Visualization:</strong> Present development concepts to investors</li>
              <li><strong className="text-white">Seasonal Variations:</strong> Show properties in different seasons and lighting</li>
            </ul>
            
            <h3 className="text-2xl font-semibold text-white mt-8 mb-4">Proven Results</h3>
            <p>
              Real estate professionals using AI-generated visuals report 25% faster sales, 40% more inquiries, and 60% higher engagement on property listings. The ability to show potential rather than just current state has transformed the buying experience.
            </p>
            
            <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-6 mt-8">
              <h4 className="text-xl font-semibold text-indigo-400 mb-3 flex items-center">
                <Building2 className="w-5 h-5 mr-2" />
                Start Showcasing Property Potential
              </h4>
              <p className="text-gray-300">
                Create stunning property visualizations that help buyers envision their future home and accelerate your sales process.
              </p>
            </div>
          </div>
        </div>
      }
      imageGallery={imageGallery}
    />
  );
};

export default RealEstatePage;
